import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useEnvironmentType } from "../../contexts/useEnvType";

interface InactivityWarningProps {
  logout: () => void;
}

export const InactivityWarning = ({ logout }: InactivityWarningProps) => {
  const { isTactical } = useEnvironmentType();

  const timeout = 60_000 * 15; // 15 minutes in milliseconds
  const promptTimeout = 60_000; // 1 minute before logout
  const [remaining, setRemaining] = useState<number>(timeout);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);

  // Create a ref to ensure we always have the latest logout function
  const logoutRef = useRef(logout);


  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);


  // Use a stable callback that calls the current logout function
  const handleLogout = useCallback(() => {
    // Use the ref to get the current logout function
    logoutRef.current();
  }, []);

  const continueSession = () => {
    activate();
    setShowLogoutWarning(false);
  };

  const { isPrompted, getRemainingTime, activate } = useIdleTimer({
    timeout,
    onIdle: handleLogout,
    promptBeforeIdle: promptTimeout,
    stopOnIdle: true,
    debounce: 500,
    onPrompt: () => {
      setShowLogoutWarning(true);
      setRemaining(promptTimeout / 1000);
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPrompted()) {
        setRemaining(Math.ceil(getRemainingTime() / 1000));
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isPrompted]);

  return showLogoutWarning && isTactical ? (
    <Dialog maxWidth="sm" open={showLogoutWarning} onClose={continueSession}>
      <DialogTitle>Inactivity Warning</DialogTitle>
      <DialogContent sx={{ minWidth: "600px" }}>
        <Typography>You have been inactive for a while. You will be logged out in {remaining} seconds</Typography>
      </DialogContent>
      <DialogActions>
        <Paper elevation={0}>
          <Button onClick={continueSession}>Continue Session</Button>
        </Paper>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
};
